/**
 * "An IngeniousPro Product" — the company mark behind Connect.
 *
 * Ported from SVB Gold's `ProductAttribution`, so the two products carry the
 * mark identically, and from its Flutter twin in `connect_mobile_shared`. Three
 * details are not obvious and all three are deliberate.
 *
 * **The logo is a lockup, not a symbol.** The asset already contains the word
 * "IngeniousPro" beside the shield, so the mark is a sentence — "An", the
 * lockup, "Product" — rather than an icon with a label.
 *
 * **The text is cap-height matched to the wordmark, not to the image.** The
 * wordmark's capitals are 55% of the image's height (measured from the leading
 * "I", not the tittle of the "i", which sits above the cap line), and the font's
 * cap height is 0.706 of its em, so the matching font size is about 0.779 of the
 * logo height — which is where {@link SIZES} comes from.
 *
 * **The logo is dropped by 30% of its height.** An image's baseline in a line of
 * text is its bottom edge, but the wordmark's baseline sits at 70% of the image.
 * The shift moves pixels, not the layout box, which leaves a gap above the ink
 * and an overhang below; padding the bottom by twice the shift re-centres the
 * ink in the box a container centres. Padding, not a negative margin: a margin
 * moves what the baseline is measured from and drops "An" and "Product" off the
 * wordmark's line.
 *
 * Both artworks are rendered and CSS shows the one for the theme, so the right
 * one is there on the first paint — the theme class is set before React runs.
 * The assets are bundled, not fetched: a remote image on a sign-in screen is a
 * blank gap whenever that host is slow.
 */

import { cn } from '../../lib/cn';
import onDark from './ingeniouspro-on-dark.png';
import onLight from './ingeniouspro-on-light.png';

/** Where the mark links to. */
export const INGENIOUSPRO_URL = 'https://www.ingeniouspro.co.in/';

/** Logo height in pixels, with the cap-height-matched font size for each. */
export const SIZES = {
  /** A sidebar or page foot, where it must not compete for attention. */
  small: { logo: 17, font: 13 },
  /** Sign-in screens. The default. */
  medium: { logo: 22, font: 17 },
  /** Splash and about screens only. */
  large: { logo: 26, font: 20 },
} as const;

export type AttributionSize = keyof typeof SIZES;

/** The proportion of the image height below the wordmark's baseline. */
const BASELINE_OFFSET = 0.3;

/** Matches the wordmark. Segoe UI is a Windows font, so the stack falls through
 *  to each platform's own UI face — close enough that the mark reads as one. */
const FONT_STACK = '"Segoe UI", system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif';

export interface ProductAttributionProps {
  size?: AttributionSize;
  /** Wrap it in a link to ingeniouspro.co.in. Off inside an existing link. */
  linked?: boolean;
  className?: string;
}

export function ProductAttribution({
  size = 'medium',
  linked = true,
  className,
}: ProductAttributionProps) {
  const { logo, font } = SIZES[size];
  const shift = logo * BASELINE_OFFSET;
  const image = { height: `${logo}px`, top: `${shift}px` };

  const mark = (
    <span
      className={cn(
        // Baseline, so "An", the wordmark and "Product" share one line. Muted:
        // provenance under a product's own branding, not a call to action.
        'inline-flex items-baseline gap-[0.3em] whitespace-nowrap leading-none font-normal text-muted',
        className,
      )}
      style={{ fontFamily: FONT_STACK, fontSize: `${font}px`, paddingBottom: `${shift * 2}px` }}
    >
      <span>An</span>
      {/* The wordmark carries the name, so a screen reader hears one phrase:
          "An IngeniousPro Product". The dark copy is hidden from it. */}
      <img
        src={onLight}
        alt="IngeniousPro"
        className="relative block w-auto self-baseline dark:hidden"
        style={image}
      />
      <img
        src={onDark}
        alt=""
        aria-hidden="true"
        className="relative hidden w-auto self-baseline dark:block"
        style={image}
      />
      <span>Product</span>
    </span>
  );

  if (!linked) return mark;

  return (
    <a
      href={INGENIOUSPRO_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex no-underline transition-opacity hover:opacity-75"
    >
      {mark}
    </a>
  );
}
